import type {
  FilterDefinition,
  FilterOptions,
  IdentityOption,
  LabeledOption,
} from "@/shared/components/filters/FilterDefinition";

type FilterDefinitionOf<T extends FilterDefinition["type"]> = Extract<
  FilterDefinition,
  { type: T }
>;

/**
 * A filter picker row minus whatever needs runtime data to build.
 *
 * Every list used to hold its rows inline, next to the hooks that supply
 * identity directories and tag searches. That made the rows unreachable from
 * anything that is not the list, and the chat's View tools need them to know
 * which fields a View on that table can filter on. Splitting the static part
 * out lets both read one source: a list hydrates it with its runtime data
 * through {@link hydrateFilterDefinitions}; the tool reads it as is.
 *
 * The scalar variants are complete rows. `enum` rows carry only static option
 * lists here, since a resolver-backed enum would not be static. `set` and
 * `identity` rows are declared without `options`, which is exactly the part
 * that needs a resolver (a tag search) or a directory (principals).
 */
export type StaticFilterField =
  | FilterDefinitionOf<"string">
  | FilterDefinitionOf<"numeric">
  | FilterDefinitionOf<"date">
  | FilterDefinitionOf<"boolean">
  | (Omit<FilterDefinitionOf<"enum">, "options"> & {
      options: readonly string[] | readonly LabeledOption[];
    })
  | Omit<FilterDefinitionOf<"set">, "options">
  | Omit<FilterDefinitionOf<"identity">, "options">;

/** What a list supplies to turn a static catalog into picker rows. */
export interface FilterRuntime {
  /**
   * Principal directories for every `identity` row. Required whenever the
   * catalog has one; a list whose catalog has none passes nothing.
   */
  identityOptions?: readonly [IdentityOption, ...IdentityOption[]];
  /**
   * Options for every `set` row, typically a tag search. A catalog with a
   * `set` row and no `setOptions` gets an empty static list, which renders
   * the picker but offers nothing to pick.
   */
  setOptions?: FilterOptions;
}

/**
 * Attach a list's runtime data to a static catalog, in catalog order.
 *
 * `enum` options are copied so the picker's array is not the catalog's
 * readonly one.
 */
export function hydrateFilterDefinitions(
  fields: readonly StaticFilterField[],
  runtime: FilterRuntime,
): FilterDefinition[] {
  return fields.map((field): FilterDefinition => {
    switch (field.type) {
      case "identity":
        if (runtime.identityOptions === undefined) {
          throw new Error(
            `Filter row "${field.field}" is an identity row, but no identity ` +
              "directories were supplied to hydrate it.",
          );
        }
        return { ...field, options: runtime.identityOptions };
      case "set":
        return { ...field, options: runtime.setOptions ?? [] };
      case "enum":
        return { ...field, options: field.options.slice() };
      default:
        return field;
    }
  });
}
